// src/services/historial.js

import AuthService from "../auth/auth.js";
import UsuariosService from "./usuarios.js";

/**
 * Servicio para manejar todas las operaciones relacionadas con el historial de usuarios
 * Adaptado específicamente a la estructura de tu backend Django
 */
class HistorialService {
  constructor() {
    this.baseURL = process.env.VUE_APP_API_URL || "https://efibox.efintel.cl";
    this.endpointHistorial = "/usuarios/api/historial_usuarios/";
    this.endpointUsuario = "/usuarios/api/usuarios/";

    // Sistema de caché para optimizar requests
    this.cache = {
      historialCompleto: null, // Caché de todo el historial
      historialPorUsuario: new Map(), // Caché por ID de usuario
      ultimaActualizacion: null,
      tiempoExpiracion: 2 * 60 * 1000, // 2 minutos (historial cambia frecuentemente)
    };

    // MAPEO DE ACCIONES REALES DE TU BACKEND
    this.accionesDisponibles = {
      LOGIN: {
        label: "Inicio de sesión",
        color: "bg-teal-500",
        badge: "bg-teal-100 text-teal-800",
        icon: "login",
        descripcion: "Inició sesión en el sistema",
      },
      LOGOUT: {
        label: "Cierre de sesión",
        color: "bg-gray-500",
        badge: "bg-gray-100 text-gray-800",
        icon: "logout",
        descripcion: "Cerró sesión",
      },
      CREATE: {
        label: "Creación",
        color: "bg-green-500",
        badge: "bg-green-100 text-green-800",
        icon: "add",
        descripcion: "Creó un nuevo registro",
      },
      UPDATE: {
        label: "Modificación",
        color: "bg-blue-500",
        badge: "bg-blue-100 text-blue-800",
        icon: "edit",
        descripcion: "Modificó un registro",
      },
      DELETE: {
        label: "Eliminación",
        color: "bg-red-500",
        badge: "bg-red-100 text-red-800",
        icon: "delete",
        descripcion: "Eliminó un registro",
      },
      PERMISSION_CHANGE: {
        label: "Cambio de permisos",
        color: "bg-purple-500",
        badge: "bg-purple-100 text-purple-800",
        icon: "shield",
        descripcion: "Modificó permisos de usuario",
      },
      CONFIG_CHANGE: {
        label: "Configuración",
        color: "bg-orange-500",
        badge: "bg-orange-100 text-orange-800",
        icon: "settings",
        descripcion: "Cambió configuración del sistema",
      },
      ELIMINAR_USUARIO: {
        label: "Usuario eliminado",
        color: "bg-red-500",
        badge: "bg-red-100 text-red-800",
        icon: "delete",
        descripcion: "Se ha eliminado un usuario",
      },
    };
  }

  // ==========================================
  // MÉTODOS PRINCIPALES
  // ==========================================

  /**
   * Obtiene el historial de acciones del usuario
   * @param {number} userId - ID del usuario
   * @param {number} page - Número de página (opcional)
   * @returns {Promise<Object>} Historial paginado
   */
  async getHistorialUsuario(userId, page = 1) {
    try {
      const url = `/usuarios/api/usuarios/${userId}/historial/${page > 1 ? `?page=${page}` : ""}`;
      const response = await AuthService.authenticatedRequest(url);
      return response;
    } catch (error) {
      console.error("Error al obtener historial de usuario:", error);
      throw error;
    }
  }

  /**
   * MÉTODO PRINCIPAL: Obtener TODO el historial desde el backend
   * @param {boolean} forzarActualizacion - Ignorar caché y obtener datos frescos
   * @param {number} limite - Límite de registros (opcional, por defecto sin límite)
   * @returns {Promise<Array>} Array de registros de historial mapeados
   */
  async obtenerHistorialCompleto(forzarActualizacion = false, limite = null) {
    try {
      // Verificar caché si no se fuerza actualización
      if (!forzarActualizacion && this.esCacheValido() && this.cache.historialCompleto) {
        return limite ? this.cache.historialCompleto.slice(0, limite) : this.cache.historialCompleto;
      }

      // OBTENER USUARIOS PARA FILTRAR is_staff
      const usuarios = await UsuariosService.obtenerTodosLosUsuarios();
      const usuariosStaffIds = new Set(usuarios.filter((u) => u.is_staff === true).map((u) => u.id));

      // OBTENER DATOS REALES DE TU BACKEND
      const url = limite ? `${this.endpointHistorial}?limit=${limite}` : this.endpointHistorial;
      const response = await AuthService.authenticatedRequest(url);

      // MANEJAR RESPUESTA (tu backend puede devolver array directo o paginado)
      let registros = [];
      if (Array.isArray(response)) {
        registros = response;
      } else if (response.results && Array.isArray(response.results)) {
        registros = response.results;
      } else {
        registros = [];
      }

      // FILTRAR USUARIOS STAFF ANTES DE MAPEAR
      registros = registros.filter((registro) => !usuariosStaffIds.has(registro.id_usuario));

      // MAPEAR REGISTROS CON TU ESTRUCTURA REAL
      const registrosMapeados = registros.map((registro) => this.mapearRegistroReal(registro));

      // Ordenar por fecha (más reciente primero)
      registrosMapeados.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // Actualizar caché
      this.cache.historialCompleto = registrosMapeados;
      this.cache.ultimaActualizacion = Date.now();

      return registrosMapeados;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * OBTENER HISTORIAL DE UN USUARIO ESPECÍFICO
   * @param {number} userId - ID del usuario
   * @param {boolean} forzarActualizacion - Ignorar caché
   * @returns {Promise<Array>} Array de registros del usuario
   */
  async obtenerHistorialUsuario(userId, forzarActualizacion = false) {
    try {
      // Verificar si el usuario es staff
      const usuario = await UsuariosService.obtenerUsuarioPorId(userId);
      if (usuario.is_staff === true) {
        return [];
      }

      // Verificar caché específico por usuario
      if (!forzarActualizacion && this.cache.historialPorUsuario.has(userId)) {
        const cacheDatos = this.cache.historialPorUsuario.get(userId);
        const tiempoTranscurrido = Date.now() - cacheDatos.timestamp;

        if (tiempoTranscurrido < this.cache.tiempoExpiracion) {
          return cacheDatos.data;
        }
      }

      // OBTENER DEL BACKEND REAL
      const url = `${this.endpointUsuario}${userId}/historial/`;
      const response = await AuthService.authenticatedRequest(url);

      // Manejar respuesta
      let registros = [];
      if (Array.isArray(response)) {
        registros = response;
      } else if (response.results && Array.isArray(response.results)) {
        registros = response.results;
      } else {
        registros = [];
      }

      // Mapear registros
      const registrosMapeados = registros.map((registro) => this.mapearRegistroReal(registro));

      // Ordenar por fecha (más reciente primero)
      registrosMapeados.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // Cachear
      this.cache.historialPorUsuario.set(userId, {
        data: registrosMapeados,
        timestamp: Date.now(),
      });

      return registrosMapeados;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * OBTENER HISTORIAL FILTRADO POR TIPO DE ACCIÓN
   * @param {string|Array<string>} acciones - Acción o array de acciones a filtrar
   * @param {boolean} forzarActualizacion - Ignorar caché
   * @returns {Promise<Array>} Registros filtrados
   */
  async obtenerHistorialPorAccion(acciones, forzarActualizacion = false) {
    try {
      const accionesArray = Array.isArray(acciones) ? acciones : [acciones];

      // Obtener historial completo
      const historial = await this.obtenerHistorialCompleto(forzarActualizacion);

      // Filtrar por acciones
      const historialFiltrado = historial.filter((registro) => accionesArray.includes(registro.accion_realizada));

      return historialFiltrado;
    } catch (error) {
      return [];
    }
  }

  /**
   * OBTENER HISTORIAL RECIENTE (últimos N registros)
   * @param {number} cantidad - Cantidad de registros
   * @param {boolean} forzarActualizacion - Ignorar caché
   * @returns {Promise<Array>} Registros más recientes
   */
  async obtenerHistorialReciente(cantidad = 20, forzarActualizacion = false) {
    try {
      const historial = await this.obtenerHistorialCompleto(forzarActualizacion);
      return historial.slice(0, cantidad);
    } catch (error) {
      return [];
    }
  }

  /**
   * BUSCAR EN HISTORIAL
   * @param {string} texto - Texto a buscar
   * @param {Array<string>} accionesFiltro - Filtrar por tipos de acción (opcional)
   * @returns {Promise<Array>} Registros que coinciden
   */
  async buscarEnHistorial(texto, accionesFiltro = []) {
    try {
      const historial = await this.obtenerHistorialCompleto();
      const textoLower = texto.toLowerCase();

      let resultados = historial.filter((registro) => {
        const nombreCompleto = `${registro.nombre_usuario} ${registro.apellido_usuario}`.toLowerCase();
        const descripcion = registro.descripcion.toLowerCase();

        return nombreCompleto.includes(textoLower) || descripcion.includes(textoLower);
      });

      // Aplicar filtro de acciones si se especifica
      if (accionesFiltro.length > 0) {
        resultados = resultados.filter((r) => accionesFiltro.includes(r.accion_realizada));
      }

      return resultados;
    } catch (error) {
      return [];
    }
  }

  /**
   * OBTENER ESTADÍSTICAS DEL HISTORIAL
   * @returns {Promise<Object>} Estadísticas agregadas
   */
  async obtenerEstadisticas() {
    try {
      const historial = await this.obtenerHistorialCompleto();

      const estadisticas = {
        total: historial.length,
        porAccion: {},
        porUsuario: {},
        porDia: {},
        ultimaActividad: historial[0]?.timestamp || null,
      };

      // Contar por tipo de acción
      historial.forEach((registro) => {
        // Por acción
        const accion = registro.accion_realizada;
        estadisticas.porAccion[accion] = (estadisticas.porAccion[accion] || 0) + 1;

        // Por usuario
        const userId = registro.id_usuario;
        estadisticas.porUsuario[userId] = (estadisticas.porUsuario[userId] || 0) + 1;

        // Por día
        const fecha = new Date(registro.timestamp).toISOString().split("T")[0];
        estadisticas.porDia[fecha] = (estadisticas.porDia[fecha] || 0) + 1;
      });

      return estadisticas;
    } catch (error) {
      return {
        total: 0,
        porAccion: {},
        porUsuario: {},
        porDia: {},
        ultimaActividad: null,
      };
    }
  }

  // ==========================================
  // MAPEO ESPECÍFICO PARA TU BACKEND
  // ==========================================

  /**
   * MAPEAR REGISTRO REAL DE TU BACKEND
   * @param {Object} registroBackend - Registro tal como viene de tu API
   * @returns {Object} Registro mapeado para frontend
   */
  mapearRegistroReal(registroBackend) {
    try {
      const accionInfo = this.accionesDisponibles[registroBackend.accion_realizada] || {
        label: registroBackend.accion_realizada,
        color: "bg-gray-500",
        badge: "bg-gray-100 text-gray-800",
        icon: "info",
        descripcion: registroBackend.accion_realizada,
      };

      // 1. Construir nombre del ACTOR (quien hizo la acción)
      const nombreActor = `${registroBackend.nombre_usuario || ""} ${registroBackend.apellido_usuario || ""}`.trim() || "Usuario Sistema";

      // 2. Construir nombre del OBJETIVO (a quien se le hizo, si existe)
      const nombreObjetivo = `${registroBackend.nombre_usuario_objetivo || ""} ${registroBackend.apellido_usuario_objetivo || ""}`.trim();

      // 3. Definir la descripción final
      let descripcionFinal = "";

      if (registroBackend.detalles) {
        descripcionFinal = registroBackend.detalles;
      } else if (nombreObjetivo) {
        descripcionFinal = `${accionInfo.descripcion}: ${nombreObjetivo}`;
      } else {
        descripcionFinal = accionInfo.descripcion;
      }

      return {
        // Campos directos
        id: registroBackend.id,
        fecha_accion: registroBackend.fecha_accion,
        accion_realizada: registroBackend.accion_realizada,
        id_usuario: registroBackend.id_usuario,

        // Campos computados para la UI
        user: nombreActor,

        description: descripcionFinal,

        // Mantenemos timestamp para ordenamiento y UI
        timestamp: registroBackend.fecha_accion,

        // Traducción de códigos
        action: this.mapearAccionACodigoComponente(registroBackend.accion_realizada),

        // Estilos visuales
        color: accionInfo.color,
        badge: accionInfo.badge,
        icon: accionInfo.icon,
        label: accionInfo.label,

        // Datos extra
        usuario_objetivo_id: registroBackend.usuario_objetivo,
        es_demo: registroBackend.demo,

        ip: "No disponible",
        location: "Chile",
      };
    } catch (error) {
      return this.crearRegistroFallback(registroBackend.id || "error");
    }
  }

  /**
   * Mapear acción del backend al código usado en el componente
   * @param {string} accionBackend - Acción del backend (LOGIN, LOGOUT, etc.)
   * @returns {string} Código para el componente
   */
  mapearAccionACodigoComponente(accionBackend) {
    const mapeo = {
      LOGIN: "inicio_sesion",
      LOGOUT: "cierre_sesion",
      CREATE: "usuario_creado",
      UPDATE: "modificacion",
      DELETE: "eliminacion",
      ELIMINAR_USUARIO: "eliminacion",
      CREAR_USUARIO: "usuario_creado",
      EDITAR_USUARIO: "modificacion",
      PERMISSION_CHANGE: "permiso",
      CONFIG_CHANGE: "configuracion",
    };

    return mapeo[accionBackend] || "modificacion";
  }

  /**
   * Crear registro fallback cuando hay errores
   * @param {number|string} registroId - ID del registro
   * @returns {Object} Registro con datos mínimos
   */
  crearRegistroFallback(registroId) {
    return {
      id: registroId,
      nombre_usuario: "Usuario",
      apellido_usuario: "Desconocido",
      fecha_accion: new Date().toISOString(),
      accion_realizada: "UNKNOWN",
      id_usuario: 0,
      user: "Usuario Desconocido",
      action: "modificacion",
      description: "Acción no disponible",
      details: "Información no disponible",
      timestamp: new Date().toISOString(),
      color: "bg-gray-500",
      badge: "bg-gray-100 text-gray-800",
      icon: "info",
      label: "Desconocido",
      ip: "No disponible",
      location: "No disponible",
      _mapeadoEn: new Date().toISOString(),
      _fuente: "fallback",
    };
  }

  // ==========================================
  // MÉTODOS DE AGRUPACIÓN (para el componente)
  // ==========================================

  /**
   * AGRUPAR REGISTROS POR FECHA (HOY, AYER, etc.)
   * @param {Array} registros - Array de registros
   * @returns {Array} Registros agrupados con labels
   */
  agruparPorFecha(registros) {
    const grupos = {};
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const ayer = new Date(hoy);
    ayer.setDate(ayer.getDate() - 1);

    registros.forEach((registro) => {
      const fecha = new Date(registro.timestamp);
      fecha.setHours(0, 0, 0, 0);

      let label;
      if (fecha.getTime() === hoy.getTime()) {
        label = "HOY";
      } else if (fecha.getTime() === ayer.getTime()) {
        label = "AYER";
      } else {
        label = fecha
          .toLocaleDateString("es-CL", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })
          .toUpperCase();
      }

      if (!grupos[label]) {
        grupos[label] = {
          fecha: fecha.getTime(),
          label,
          registros: [],
        };
      }

      grupos[label].registros.push(registro);
    });

    // Ordenar grupos por fecha (más reciente primero)
    return Object.values(grupos).sort((a, b) => b.fecha - a.fecha);
  }

  /**
   * Obtener conteo de registros por tipo de acción
   * @param {Array} registros - Array de registros
   * @returns {Object} Objeto con conteos por acción
   */
  obtenerConteosPorAccion(registros) {
    const conteos = {};

    registros.forEach((registro) => {
      const action = registro.action; // Código del componente
      conteos[action] = (conteos[action] || 0) + 1;
    });

    return conteos;
  }

  // ==========================================
  // GESTIÓN DE CACHÉ
  // ==========================================

  /**
   * Verificar si el caché global es válido
   * @returns {boolean} True si el caché es válido
   */
  esCacheValido() {
    if (!this.cache.ultimaActualizacion) return false;

    const tiempoTranscurrido = Date.now() - this.cache.ultimaActualizacion;
    return tiempoTranscurrido < this.cache.tiempoExpiracion;
  }

  /**
   * Limpiar caché manualmente
   */
  limpiarCache() {
    this.cache.historialCompleto = null;
    this.cache.historialPorUsuario.clear();
    this.cache.ultimaActualizacion = null;
  }

  /**
   * Obtener estadísticas del caché
   * @returns {Object} Estadísticas del caché
   */
  obtenerEstadisticasCache() {
    return {
      historialCacheado: !!this.cache.historialCompleto,
      totalRegistros: this.cache.historialCompleto?.length || 0,
      usuariosEnCache: this.cache.historialPorUsuario.size,
      ultimaActualizacion: this.cache.ultimaActualizacion,
      esValido: this.esCacheValido(),
      tiempoExpiracion: this.cache.tiempoExpiracion,
      tiempoRestante: this.cache.ultimaActualizacion
        ? Math.max(0, this.cache.tiempoExpiracion - (Date.now() - this.cache.ultimaActualizacion))
        : 0,
    };
  }

  // ==========================================
  // MANEJO DE ERRORES
  // ==========================================

  /**
   * Manejar errores de las llamadas a la API
   * @param {Error} error - Error original
   * @returns {Error} Error procesado
   */
  manejarError(error) {
    let mensaje = "Error desconocido al obtener historial";
    let codigo = "HISTORIAL_ERROR";

    if (error.code === "ECONNABORTED") {
      mensaje = "Tiempo de espera agotado al obtener historial";
      codigo = "HISTORIAL_TIMEOUT";
    } else if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 401:
          mensaje = "No autorizado para obtener historial";
          codigo = "HISTORIAL_UNAUTHORIZED";
          break;
        case 403:
          mensaje = "Sin permisos para acceder al historial";
          codigo = "HISTORIAL_FORBIDDEN";
          break;
        case 404:
          mensaje = "Historial no encontrado";
          codigo = "HISTORIAL_NOT_FOUND";
          break;
        default:
          mensaje = `Error del servidor al obtener historial (${status})`;
          codigo = "HISTORIAL_SERVER_ERROR";
      }
    } else if (error.request) {
      mensaje = "Error de conexión al obtener historial";
      codigo = "HISTORIAL_NETWORK_ERROR";
    }

    const errorProcesado = new Error(mensaje);
    errorProcesado.codigo = codigo;
    errorProcesado.original = error;

    return errorProcesado;
  }
}

// Exportar instancia única del servicio
export default new HistorialService();
